// Module-level stores that keep "row action in progress" flags alive when the
// user navigates away from a page and comes back. Without this, starting a
// retry / delete / restore and then switching tabs loses the button's loading
// state even though the underlying request is still running.
//
// Pattern mirrors UploadPdf/uploadPdfStore.js (module state read through
// useSyncExternalStore), extended with a per-flag timestamp so a request that
// never settles can't disable a button forever.

const STALE_AFTER_MS = 90_000;

function createActionFlagStore(keys) {
  const timestamps = {};

  let state = keys.reduce((acc, key) => {
    acc[key] = "";
    return acc;
  }, {});

  const listeners = new Set();

  const emit = () => listeners.forEach((listener) => listener());

  const getSnapshot = () => state;

  const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  // Set a flag to a row id, or "" to clear it.
  const set = (key, id) => {
    const value = id || "";

    if (state[key] === value) return;

    state = { ...state, [key]: value };
    timestamps[key] = value ? Date.now() : 0;

    emit();
  };

  // Clear flags left behind by a request that never settled (e.g. a hung
  // network call) so a button can't stay disabled forever. Call on mount.
  const pruneStale = () => {
    let changed = false;
    const next = { ...state };

    keys.forEach((key) => {
      if (
        next[key] &&
        timestamps[key] &&
        Date.now() - timestamps[key] > STALE_AFTER_MS
      ) {
        next[key] = "";
        timestamps[key] = 0;
        changed = true;
      }
    });

    if (changed) {
      state = next;
      emit();
    }
  };

  return { getSnapshot, subscribe, set, pruneStale };
}

// Documents page: retry / delete of a document row, plus the chunk bulk-edit
// of a document (so its Retry button can show "Processing" while the edit and
// its follow-up refresh are in flight, even across a tab switch).
export const documentsActionStore = createActionFlagStore([
  "retryingDocId",
  "deletingDocId",
  "editingDocId",
]);

// Deleted Documents page: restore / permanent delete of a document row.
export const deletedDocumentsActionStore = createActionFlagStore([
  "restoringDocId",
  "permanentlyDeletingDocId",
]);
